import { Component, OnInit, OnDestroy, ViewChild, signal, inject, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  ReactiveFormsModule,
  FormBuilder,
  FormGroup,
  Validators,
  FormControl,
} from '@angular/forms';

import { TableModule, Table } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { TextareaModule } from 'primeng/textarea';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { TooltipModule } from 'primeng/tooltip';
import { MessageModule } from 'primeng/message';
import { ConfirmationService, MessageService } from 'primeng/api';

import { Api } from '../../../api/api';
import {
  adminCategoriesControllerListCategoriesV1,
  adminCategoriesControllerCreateCategoryV1,
  adminCategoriesControllerUpdateCategoryV1,
  adminCategoriesControllerDeleteCategoryV1,
} from '../../../api/functions';
import { CategoryResponseAdminDto } from '../../../api/models/category-response-admin-dto';
import { CreateCategoryDto } from '../../../api/models/create-category-dto';
import { UpdateCategoryDto } from '../../../api/models/update-category-dto';
import { HeaderService } from '../../../services/header.service';
import { debounceTime, distinctUntilChanged, Subject, takeUntil, skip } from 'rxjs';

@Component({
  selector: 'app-categories',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    ReactiveFormsModule,
    TableModule,
    ButtonModule,
    DialogModule,
    InputTextModule,
    TextareaModule,
    IconFieldModule,
    InputIconModule,
    ConfirmDialogModule,
    MessageModule,
    TooltipModule,
  ],
  providers: [ConfirmationService],
  templateUrl: './categories.component.html',
  styleUrl: './categories.component.css',
})
export class CategoriesComponent implements OnInit, OnDestroy {
  @ViewChild('table') table!: Table;
  @ViewChild('searchInput') searchInput!: ElementRef<HTMLInputElement>;

  private readonly api = inject(Api);
  private readonly fb = inject(FormBuilder);
  private readonly confirmationService = inject(ConfirmationService);
  private readonly messageService = inject(MessageService);
  private readonly translateService = inject(TranslateService);
  private readonly headerService = inject(HeaderService);

  protected readonly categories = signal<CategoryResponseAdminDto[]>([]);
  protected readonly loading = signal(false);
  protected readonly dialogVisible = signal(false);
  protected readonly dialogMode = signal<'create' | 'edit'>('create');
  protected readonly searchControl = new FormControl('');

  protected categoryForm!: FormGroup;
  private editingCategory: CategoryResponseAdminDto | null = null;
  private readonly destroy$ = new Subject<void>();
  private isSearching = false;

  ngOnInit(): void {
    this.headerService.setTitle(this.translateService.instant('categories.title'));
    this.headerService.setSubtitle(this.translateService.instant('categories.subtitle'));

    this.initForm();
    this.loadCategories();

    // Setup server-side search with debounce
    this.searchControl.valueChanges
      .pipe(skip(1), takeUntil(this.destroy$), debounceTime(300), distinctUntilChanged())
      .subscribe((searchTerm) => {
        this.isSearching = true;
        this.loadCategories(searchTerm || undefined);
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  initForm(): void {
    this.categoryForm = this.fb.group({
      label: ['', Validators.required],
      description: [''],
    });
  }

  protected loadCategories(search?: string): void {
    this.loading.set(true);
    this.searchControl.disable();

    this.api
      .invoke(adminCategoriesControllerListCategoriesV1, { search })
      .then((response) => {
        this.categories.set(response);
      })
      .catch((error) => {
        console.error('Error loading categories:', error);
        this.messageService.add({
          severity: 'error',
          summary: this.translateService.instant('common.error'),
          detail: this.translateService.instant('categories.loadError'),
        });
      })
      .finally(() => {
        this.loading.set(false);
        // Avoid triggering search valueChanges on re-enable
        this.searchControl.enable({ emitEvent: false });

        if (this.isSearching && this.searchInput) {
          setTimeout(() => {
            this.searchInput.nativeElement.focus();
            this.isSearching = false;
          }, 0);
        }
      });
  }

  protected openCreateDialog(): void {
    this.dialogMode.set('create');
    this.editingCategory = null;
    this.categoryForm.reset();
    this.dialogVisible.set(true);
  }

  protected openEditDialog(category: CategoryResponseAdminDto): void {
    this.dialogMode.set('edit');
    this.editingCategory = category;
    this.categoryForm.patchValue({
      label: category.label,
      description: category.description || '',
    });
    this.dialogVisible.set(true);
  }

  protected saveCategory(): void {
    if (this.categoryForm.invalid) {
      Object.keys(this.categoryForm.controls).forEach((key) => {
        this.categoryForm.get(key)?.markAsTouched();
      });
      return;
    }

    this.loading.set(true);

    if (this.dialogMode() === 'create') {
      this.createCategory();
    } else {
      this.updateCategory();
    }
  }

  private createCategory(): void {
    const dto: CreateCategoryDto = {
      label: this.categoryForm.value.label,
      description: this.categoryForm.value.description || undefined,
    };

    this.api
      .invoke(adminCategoriesControllerCreateCategoryV1, { body: dto })
      .then((created) => {
        this.messageService.add({
          severity: 'success',
          summary: this.translateService.instant('common.success'),
          detail: this.translateService.instant('categories.createSuccess'),
        });
        this.dialogVisible.set(false);
        // Optimistically add to list to avoid extra reload
        const current = this.categories();
        this.categories.set([...current, created]);
        this.loading.set(false);
      })
      .catch((error) => {
        console.error('Error creating category:', error);
        this.messageService.add({
          severity: 'error',
          summary: this.translateService.instant('common.error'),
          detail: error.error?.message || this.translateService.instant('categories.createError'),
        });
        this.loading.set(false);
      });
  }

  private updateCategory(): void {
    if (!this.editingCategory) return;

    const dto: UpdateCategoryDto = {
      label: this.categoryForm.value.label,
      description: this.categoryForm.value.description || undefined,
    };

    this.api
      .invoke(adminCategoriesControllerUpdateCategoryV1, {
        id: this.editingCategory.id,
        body: dto,
      })
      .then((updated) => {
        this.messageService.add({
          severity: 'success',
          summary: this.translateService.instant('common.success'),
          detail: this.translateService.instant('categories.updateSuccess'),
        });
        this.dialogVisible.set(false);
        // Optimistically update local list
        const current = this.categories();
        const next = current.map((c) => (c.id === updated.id ? { ...c, ...updated } : c));
        this.categories.set(next);
        this.loading.set(false);
      })
      .catch((error) => {
        console.error('Error updating category:', error);
        this.messageService.add({
          severity: 'error',
          summary: this.translateService.instant('common.error'),
          detail: error.error?.message || this.translateService.instant('categories.updateError'),
        });
        this.loading.set(false);
      });
  }

  protected deleteCategory(category: CategoryResponseAdminDto): void {
    this.confirmationService.confirm({
      message: this.translateService.instant('categories.deleteConfirmMessage', {
        name: category.label,
      }),
      header: this.translateService.instant('categories.deleteConfirmHeader'),
      icon: 'pi pi-exclamation-triangle',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => {
        this.loading.set(true);

        this.api
          .invoke(adminCategoriesControllerDeleteCategoryV1, { id: category.id })
          .then(() => {
            this.messageService.add({
              severity: 'success',
              summary: this.translateService.instant('common.success'),
              detail: this.translateService.instant('categories.deleteSuccess'),
            });
            // Optimistically update the local list to avoid double refresh/network call
            const current = this.categories();
            this.categories.set(current.filter((c) => c.id !== category.id));
            this.loading.set(false);
          })
          .catch((error) => {
            console.error('Error deleting category:', error);
            this.messageService.add({
              severity: 'error',
              summary: this.translateService.instant('common.error'),
              detail:
                error.error?.message || this.translateService.instant('categories.deleteError'),
            });
            this.loading.set(false);
          });
      },
    });
  }

  protected getDialogTitle(): string {
    return this.dialogMode() === 'create'
      ? this.translateService.instant('categories.createTitle')
      : this.translateService.instant('categories.editTitle');
  }

  protected isFieldInvalid(fieldName: string): boolean {
    const field = this.categoryForm.get(fieldName);
    return !!(field && field.invalid && field.touched);
  }

  protected getFieldError(fieldName: string): string {
    const field = this.categoryForm.get(fieldName);
    if (!field || !field.errors || !field.touched) return '';

    if (field.errors['required']) {
      return this.translateService.instant('validation.required');
    }
    return '';
  }

  protected getDeleteTooltip(category: CategoryResponseAdminDto): string | undefined {
    if (!category) return undefined;
    const count = category.projectCount ?? 0;
    if (count > 0) {
      const key = 'categories.inUseTooltip';
      const translated = this.translateService.instant(key, { count });
      // Fallback to English text if translation key missing
      return translated === key ? `Cannot delete: used by ${count} project(s)` : translated;
    }
    return undefined;
  }
}
